import { c as _c } from "react/compiler-runtime";
import { endOfMonth, endOfQuarter, endOfWeek, endOfYear, isAfter, isBefore, max, min, startOfDay, startOfMonth, startOfQuarter, startOfToday, startOfWeek, startOfYear, startOfYesterday, subDays, subMonths, subQuarters, subWeeks, subYears } from 'shared/dateFns';
import { CUSTOM_PRESET_VALUE, type DatePreset, type DateRange, type PresetsProps } from '@precis-digital/kurama/DatePickerV2';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'shared/translations';
const ALL_TIME_START_DATE = new Date(2000, 0, 1);
const WEEK_STARTS_ON = 1 as const;
export type DatePickerV2PresetsMode = 'range' | 'single';
export const DATE_PICKER_V2_PRESET_VALUES = ['today', 'yesterday', 'last7Days', 'last14Days', 'last28Days', 'last30Days', 'lastWeek', 'lastMonth', 'lastQuarter', 'lastYear', 'thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'allTime'] as const;
export type DatePickerV2PresetValue = (typeof DATE_PICKER_V2_PRESET_VALUES)[number];
const DATE_PICKER_V2_RANGE_PRESET_LABEL_KEYS = {
  today: 'datePickerV2.presets.today',
  yesterday: 'datePickerV2.presets.yesterday',
  last7Days: 'datePickerV2.presets.last7Days',
  last14Days: 'datePickerV2.presets.last14Days',
  last28Days: 'datePickerV2.presets.last28Days',
  last30Days: 'datePickerV2.presets.last30Days',
  lastWeek: 'datePickerV2.presets.lastWeek',
  lastMonth: 'datePickerV2.presets.lastMonth',
  lastQuarter: 'datePickerV2.presets.lastQuarter',
  lastYear: 'datePickerV2.presets.lastYear',
  thisWeek: 'datePickerV2.presets.thisWeek',
  thisMonth: 'datePickerV2.presets.thisMonth',
  thisQuarter: 'datePickerV2.presets.thisQuarter',
  thisYear: 'datePickerV2.presets.thisYear',
  allTime: 'datePickerV2.presets.allTime'
} as const satisfies Record<DatePickerV2PresetValue, string>;

/** Labels for single-date mode; shared with range where the meaning is unchanged. */
const DATE_PICKER_V2_SINGLE_PRESET_LABEL_KEYS = {
  today: 'datePickerV2.presets.today',
  yesterday: 'datePickerV2.presets.yesterday',
  last7Days: 'datePickerV2.singlePresets.last7Days',
  last14Days: 'datePickerV2.singlePresets.last14Days',
  last28Days: 'datePickerV2.singlePresets.last28Days',
  last30Days: 'datePickerV2.singlePresets.last30Days',
  lastWeek: 'datePickerV2.singlePresets.lastWeek',
  lastMonth: 'datePickerV2.singlePresets.lastMonth',
  lastQuarter: 'datePickerV2.singlePresets.lastQuarter',
  lastYear: 'datePickerV2.singlePresets.lastYear',
  thisWeek: 'datePickerV2.singlePresets.thisWeek',
  thisMonth: 'datePickerV2.singlePresets.thisMonth',
  thisQuarter: 'datePickerV2.singlePresets.thisQuarter',
  thisYear: 'datePickerV2.singlePresets.thisYear',
  allTime: 'datePickerV2.presets.allTime'
} as const satisfies Record<DatePickerV2PresetValue, string>;
const DATE_PRESET_VALUES: ReadonlySet<string> = new Set(DATE_PICKER_V2_PRESET_VALUES);
export const isDatePickerV2PresetValue = (value: string): value is DatePickerV2PresetValue => DATE_PRESET_VALUES.has(value);
const toPointRange = (date: Date): DateRange => ({
  from: date,
  to: date
});
const isRangeFullyOutsideBounds = (range: DateRange, minDate?: Date, maxDate?: Date): boolean => {
  const start = startOfDay(range.from);
  const end = startOfDay(range.to ?? range.from);
  if (minDate != null && isBefore(end, startOfDay(minDate))) return true;
  if (maxDate != null && isAfter(start, startOfDay(maxDate))) return true;
  return false;
};

/**
 * Caps a calendar period at `reference` (the latest day with data) without letting the end fall
 * before the start. Consumers persist presets by name and re-resolve them later, so an inverted
 * range would reach analytics `BETWEEN` filters — for example `thisMonth` on the 1st of a month
 * while dashboards only report through yesterday.
 */
const getCappedPeriod = (from: Date, periodEnd: Date, reference: Date): DateRange => ({
  from,
  to: max([from, min([reference, periodEnd])])
});
const getDateRangeFromRangePreset = (presetValue: DatePickerV2PresetValue, maxDate: Date = startOfToday()): DateRange => {
  const reference = startOfDay(maxDate);
  const today = startOfToday();
  switch (presetValue) {
    case 'today':
      return {
        from: today,
        to: today
      };
    case 'yesterday':
      {
        const yesterday = startOfYesterday();
        return {
          from: yesterday,
          to: yesterday
        };
      }
    case 'last7Days':
      return {
        from: subDays(reference, 6),
        to: reference
      };
    case 'last14Days':
      return {
        from: subDays(reference, 13),
        to: reference
      };
    case 'last28Days':
      return {
        from: subDays(reference, 27),
        to: reference
      };
    case 'last30Days':
      return {
        from: subDays(reference, 29),
        to: reference
      };
    case 'lastWeek':
      {
        const lastWeek = subWeeks(today, 1);
        return getCappedPeriod(startOfWeek(lastWeek, {
          weekStartsOn: WEEK_STARTS_ON
        }), endOfWeek(lastWeek, {
          weekStartsOn: WEEK_STARTS_ON
        }), reference);
      }
    case 'lastMonth':
      {
        const lastMonth = subMonths(today, 1);
        return getCappedPeriod(startOfMonth(lastMonth), endOfMonth(lastMonth), reference);
      }
    case 'lastQuarter':
      {
        const lastQuarter = subQuarters(today, 1);
        return getCappedPeriod(startOfQuarter(lastQuarter), endOfQuarter(lastQuarter), reference);
      }
    case 'lastYear':
      {
        const lastYear = subYears(today, 1);
        return getCappedPeriod(startOfYear(lastYear), endOfYear(lastYear), reference);
      }
    case 'thisWeek':
      return getCappedPeriod(startOfWeek(today, {
        weekStartsOn: WEEK_STARTS_ON
      }), endOfWeek(today, {
        weekStartsOn: WEEK_STARTS_ON
      }), reference);
    case 'thisMonth':
      return getCappedPeriod(startOfMonth(today), endOfMonth(today), reference);
    case 'thisQuarter':
      return getCappedPeriod(startOfQuarter(today), endOfQuarter(today), reference);
    case 'thisYear':
      return getCappedPeriod(startOfYear(today), endOfYear(today), reference);
    case 'allTime':
      return {
        from: ALL_TIME_START_DATE,
        to: reference
      };
    default:
      return presetValue satisfies never;
  }
};

/**
 * Single-date counterparts of the range presets, returned as point ranges (`from === to`) so
 * Kurama's DatePickerV2 preset API (which always resolves to a `DateRange`) stays unchanged.
 */
const getDateRangeFromSinglePreset = (presetValue: DatePickerV2PresetValue, maxDate: Date = startOfToday()): DateRange => {
  const reference = startOfDay(maxDate);
  const today = startOfToday();
  switch (presetValue) {
    case 'today':
      return toPointRange(today);
    case 'yesterday':
      return toPointRange(startOfYesterday());
    case 'last7Days':
      return toPointRange(subDays(reference, 7));
    case 'last14Days':
      return toPointRange(subDays(reference, 14));
    case 'last28Days':
      return toPointRange(subDays(reference, 28));
    case 'last30Days':
      return toPointRange(subDays(reference, 30));
    case 'lastWeek':
      return toPointRange(subWeeks(today, 1));
    case 'lastMonth':
      return toPointRange(subMonths(today, 1));
    case 'lastQuarter':
      return toPointRange(subQuarters(today, 1));
    case 'lastYear':
      return toPointRange(subYears(today, 1));
    case 'thisWeek':
      return toPointRange(startOfWeek(today, {
        weekStartsOn: WEEK_STARTS_ON
      }));
    case 'thisMonth':
      return toPointRange(startOfMonth(today));
    case 'thisQuarter':
      return toPointRange(startOfQuarter(today));
    case 'thisYear':
      return toPointRange(startOfYear(today));
    case 'allTime':
      return toPointRange(ALL_TIME_START_DATE);
    default:
      return presetValue satisfies never;
  }
};
export const getDateRangeFromDatePickerV2Preset = (presetValue: DatePickerV2PresetValue, maxDate: Date = startOfToday(), mode: DatePickerV2PresetsMode = 'range'): DateRange | null => mode === 'single' ? getDateRangeFromSinglePreset(presetValue, maxDate) : getDateRangeFromRangePreset(presetValue, maxDate);
const createResolveDatePickerV2Preset = (mode: DatePickerV2PresetsMode): PresetsProps['getDateRangeFromPreset'] => (presetValue, maxDate) => isDatePickerV2PresetValue(presetValue) ? getDateRangeFromDatePickerV2Preset(presetValue, maxDate, mode) : null;
export const getDatePickerV2Presets = (t: TFunction, maxDate: Date = startOfDay(new Date()), minDate?: Date, mode: DatePickerV2PresetsMode = 'range'): DatePreset[] => {
  const reference = startOfDay(maxDate);
  const today = startOfToday();
  const yesterday = startOfYesterday();
  const labelKeys = mode === 'single' ? DATE_PICKER_V2_SINGLE_PRESET_LABEL_KEYS : DATE_PICKER_V2_RANGE_PRESET_LABEL_KEYS;
  const presets = DATE_PICKER_V2_PRESET_VALUES.filter(value => {
    if (value === 'today') return !isBefore(reference, today);
    if (value === 'yesterday') return !isBefore(reference, yesterday);
    return true;
  }).map(value => {
    const range = getDateRangeFromDatePickerV2Preset(value, reference, mode);
    return {
      label: t(labelKeys[value]),
      value,
      disabled: range == null || isRangeFullyOutsideBounds(range, minDate, reference)
    };
  });
  return [{
    label: t('datePickerV2.presets.custom'),
    value: CUSTOM_PRESET_VALUE
  }, ...presets];
};
interface UseDatePickerV2PresetsOptions {
  minDate?: Date;
  maxDate?: Date;
  showPresets?: boolean;
  /**
   * Whether presets resolve to date ranges or single dates (point ranges with `from === to`).
   * Defaults to `'range'`.
   */
  mode?: DatePickerV2PresetsMode;
}
export const useDatePickerV2Presets = t0 => {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    minDate,
    maxDate,
    showPresets: t2,
    mode: t3
  } = t1;
  const showPresets = t2 === undefined ? true : t2;
  const mode = t3 === undefined ? "range" : t3;
  const {
    t
  } = useTranslation("common");
  let t4;
  if ($[2] !== minDate) {
    t4 = minDate?.getTime();
    $[2] = minDate;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const minTime = t4;
  let t5;
  if ($[4] !== maxDate) {
    t5 = maxDate?.getTime();
    $[4] = maxDate;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const maxTime = t5;
  let t6;
  if ($[6] !== maxTime || $[7] !== minTime || $[8] !== mode || $[9] !== t) {
    t6 = getDatePickerV2Presets(t, maxTime == null ? undefined : new Date(maxTime), minTime == null ? undefined : new Date(minTime), mode);
    $[6] = maxTime;
    $[7] = minTime;
    $[8] = mode;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== mode) {
    t7 = createResolveDatePickerV2Preset(mode);
    $[11] = mode;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== showPresets || $[14] !== t6 || $[15] !== t7) {
    t8 = {
      showPresets,
      presets: t6,
      getDateRangeFromPreset: t7
    };
    $[13] = showPresets;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};